import { useState, useEffect, useCallback } from 'react';
import { Row, Col, Badge, Calendar, DatePicker, message, Tooltip, Typography, Space, Upload } from "antd";
import cx from 'classnames';
import { ReModal, ReButton } from "@/components/refineElement";
import { showDelConfirm, getOnOkFunction } from "@/lib/antdElementFunction";
import {
  startThisMonth,
  endThisMonth,
  convertToTimestamp,
  dateFormat,
  startTimeToday,
  convertToTimestamp10,
  getDateCellDataArray,
  disabledMonth,
  getMoment,
  dateTimeFormat5,
  dateTimeFormat6,
  dateTimeFormat7,
  dateTimeFormat8,
  getDayVacationDateParams,
  getDayMakeUpDateParams,
  startThisYear,
  endThisYear,
} from "@/lib/dateTime";
import AnnouncementConfig from "@/config/announcement";
import DepartmentConfig from "@/config/department";
import RollCallonfig from "@/config/rollCall";
import { getLangList } from '@/lib/i18n';
import { filterScheduleArrayByTarget, getActionDepartmentArray } from "@/lib/schedule";
import { getStorage } from "@/lib/webStorage";
import { hasPermission } from "@/lib/permissionUtil";
import { excelUploadProps } from "@/lib/uploadFunction";
import { exportExcel } from "@/lib/exlsDownload";
import { SpecialDateArrayType } from './types';
import ModalInfo from "./ModalInfo";
import ModalBatchDelete from "./ModalBatchDelete";
import "./index.less";

const {
  getCalendarInfo,
  createCalendarInfo,
  updateCalendarInfo,
  deleteCalendarInfo,
  importCalendarFile,
  exportCalendar,
} = AnnouncementConfig;
const { getDepartmentInfo } = DepartmentConfig;
const { getVacationAndMakeUpDay } = RollCallonfig;

const targetColorObject: any = {
  0: 'orange', // 全部
  1: 'green', // 學生
  2: 'pink', // 老師
}

function Schedule() {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalBatchDeleteVisible, setIsModalBatchDeleteVisible] = useState(false);
  const [handleType, setHandleType] = useState('');
  const [dataArray, setDataArray] = useState([]);
  const [dataItemInfo, setDataItemInfo]: any = useState({});
  const [selectedDate, setSelectedDate] = useState(startTimeToday);
  const [startTime, setStartTime]: any = useState(startThisMonth);
  const [departmentArray, setDepartmentArray]: any = useState([]);
  const [endTime, setEndTime]: any = useState(endThisMonth);
  const [specialDateArray, setSpecialDateArray] = useState<SpecialDateArrayType[]>([]);
  const filterDepartmentArray = departmentArray.filter((item: any) => item.id !== 1); // 過濾掉學生部門
  const roleId = +getStorage("roleId");
  const scheduleT: any = getLangList("schedule");
  const errorT: any = getLangList("error");
  const errorCodeT: any = getLangList("errorCode");
  const buttonT: any = getLangList("button");
  const menuTitleT: any = getLangList("menuTitle");

  let isClickDate: boolean = true;

  const excelTitle = [
    'ID',
    `${scheduleT.START}${scheduleT.TIME}`,
    `${scheduleT.END}${scheduleT.TIME}`,
    scheduleT.TITLE,
    scheduleT.IMPORTANT_ANNOUNCEMENT,
    scheduleT.ANNOUNCEMENT_TARGET,
    scheduleT.ANNOUNCEMENT_PLACE,
    scheduleT.CONTENT,
    scheduleT.IS_VACATION,
    scheduleT.IS_MAKE_UP_DATE,
    scheduleT.MAKE_UP_DATE,
    scheduleT.IS_SHOW_HOLIDAY,
  ];

  const getVacationAndMakeUpDayFunction = useCallback(() => {
    const params = { startTime: convertToTimestamp(startTime), endTime: convertToTimestamp(endTime) };

    getVacationAndMakeUpDay(params).then((res: any) => {
      const specialDateArray = res?.value ?? [];

      setSpecialDateArray(specialDateArray);
    });
  }, [startTime, endTime]);

  const getCalendarInfoFunction = useCallback(() => {
    const params = { startTime: convertToTimestamp(startTime), endTime: convertToTimestamp(endTime) };

    getCalendarInfo(params).then((res: any) => {
      const dataArray = res?.value ?? {};
      setDataArray(dataArray);
    });
  }, [startTime, endTime]);

  useEffect(() => {
    getCalendarInfoFunction();
    getVacationAndMakeUpDayFunction();
  }, [getCalendarInfoFunction, getVacationAndMakeUpDayFunction]);

  useEffect(() => {
    getDepartmentInfo().then((res: any) => {
      const departmentArray = res?.value ?? {};
      setDepartmentArray(departmentArray);
    });
  }, []);

  const changeMonth = (value: any, onChange: any) => {
    const startTime = convertToTimestamp(value.startOf("month"));
    const endTime = convertToTimestamp(value.endOf("month"));

    isClickDate = false;
    onChange(value);
    isClickDate = true;
    setStartTime(startTime);
    setEndTime(endTime);
  }

  const toggleModal = (handleType: string = '') => {
    setIsModalVisible(!isModalVisible);
    setHandleType(handleType);
  };

  const clickCreate = (value: any) => {
    if (isClickDate) {
      setDataItemInfo({});
      setSelectedDate(value.format(dateFormat));
      toggleModal('create');
    }
  }

  const clickEdit = (item: any) => (e: any) => {
    e.stopPropagation();
    setDataItemInfo(item);
    toggleModal('update');
  }

  const clickDelete = () => {
    const { id } = dataItemInfo;
    showDelConfirm(() => deleteCalendarInfoFunction([id], 'single'));
  };

  function clickImportCalendarFile(e: any) {
    const { file, onSuccess, onError } = e;
    const params = { fileFormat: { file } };
    const isTooLarge = file.size / 1024 / 1024 > 50;

    if (isTooLarge) {
      message.error(errorT.IMAGE_TOO_LARGE);
      return onError();
    }

    importCalendarFile(params).then((res: any) => {
      if (!res.success) {
        message.error(errorCodeT[res.errorCode] ?? errorT.failed);
        return onError();
      }
      onSuccess();
      message.success(errorT.success);
      getCalendarInfoFunction();
      getVacationAndMakeUpDayFunction();
    });
  };

  function clickUserExport() {
    const params = {
      startTime: convertToTimestamp10(startThisYear),
      endTime: convertToTimestamp10(endThisYear),
    };

    exportCalendar(params).then((res: any) => {
      const { success, value } = res;
      if (!success) {
        return message.error(errorT.failed);
      }
      const fileTitle = `${menuTitleT.schedule}${startThisYear.format(dateTimeFormat6)}`;
      const rowData = value.map((item: any) => [
        item.id ?? '',
        item.startTime ? getMoment(item.startTime).format(dateTimeFormat8) : '',
        item.endTime ? getMoment(item.endTime).format(dateTimeFormat8) : '',
        item.title ?? '',
        item.isFocus ? 'TRUE' : 'FALSE',
        item.personType ?? '',
        departmentArray?.find((department: any) => department.id === item.departmentId)?.departmentName ?? '',
        item.content ?? '',
        item.isVacation ? 'TRUE' : 'FALSE',
        item.isMakeUpDay ? 'TRUE' : 'FALSE',
        item.makeUpDate ? getMoment(item.makeUpDate).format(dateTimeFormat8) : '',
        item.isShowHoliday ? 'TRUE' : 'FALSE',
      ]);

      exportExcel({
        data: [excelTitle, ...rowData],
        workbookName: fileTitle,
      });
    });
  };

  const createCalendarInfoFunction = (values: any) => {
    const { title, isFocus, personType, departmentId, content, date } = values;
    const departmentIdArray = getActionDepartmentArray(filterDepartmentArray, 1).map((item: any) => item.id); // 可 create 的 id[]
    const params = {
      title,
      isFocus,
      personType: personType.length === 2 ? 0 : personType[0], // 0全選  1學生  2 行政人員
      departmentId,
      content: typeof content === 'object' ? content.toHTML() : '',
      startTime: convertToTimestamp10(date[0]),
      endTime: convertToTimestamp10(date[1]),
      isVacation: 0,
      isMakeUpDay: 0,
      makeUpDate: null,
    };

    if (departmentIdArray.indexOf(departmentId) === -1) {
      return message.error(errorT.NO_PERMISSION);
    }

    createCalendarInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorCodeT[res.errorCode] ?? errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getCalendarInfoFunction();
      getVacationAndMakeUpDayFunction();
    });
  }

  const updateCalendarInfoFunction = (values: any) => {
    const { id } = dataItemInfo;
    const { title, isFocus, personType, departmentId, content, date } = values;
    const departmentIdArray = getActionDepartmentArray(filterDepartmentArray, 2).map((item: any) => item.id); // 可 update 的 id[]
    const params = {
      title,
      isFocus,
      personType: personType.length === 2 ? 0 : personType[0], // 0全選  1學生  2 行政人員
      departmentId,
      content: typeof content === 'object' ? content.toHTML() : content,
      startTime: convertToTimestamp10(date[0]),
      endTime: convertToTimestamp10(date[1]),
      id,
      isVacation: 0,
      isMakeUpDay: 0,
      makeUpDate: null,
    };

    if (departmentIdArray.indexOf(departmentId) === -1) {
      return message.error(errorT.NO_PERMISSION);
    }

    updateCalendarInfo(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorCodeT[res.errorCode] ?? errorT.failed);
      }
      message.success(errorT.success);
      toggleModal();
      getCalendarInfoFunction();
      getVacationAndMakeUpDayFunction();
    });
  }

  const deleteCalendarInfoFunction = (id: string[], deleteType: string) => {
    deleteCalendarInfo({ id }).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      deleteType === 'single' && toggleModal();
      deleteType === 'batch' && setIsModalBatchDeleteVisible(!isModalBatchDeleteVisible);
      getCalendarInfoFunction();
      getVacationAndMakeUpDayFunction();
    });
  }

  const okFunction = {
    create: createCalendarInfoFunction,
    update: updateCalendarInfoFunction,
  };

  const dateCellRender = (date: any) => {
    const listData = getDateCellDataArray(date, dataArray);
    const filterData = filterScheduleArrayByTarget(listData, roleId);
    const departmentIdArray = getActionDepartmentArray(filterDepartmentArray, 0).map((item: any) => item.id); // 可 view 的 id[]
    const viewData = filterData.filter((item: any) => departmentIdArray.indexOf(+item.departmentId) > -1);

    return (
      <ul className="schedule-events">
        {viewData.map((item: any, idx: any) => {
          const { personType, title, isFocus } = item;

          return title
            ? (
              <li key={idx}>
                <Tooltip title={title}>
                  <div className={cx('schedule-div', { 'is-focus': isFocus })} onClick={clickEdit(item)}>
                    <Badge color={targetColorObject[personType]} text={title} />
                  </div>
                </Tooltip>
              </li>
            )
            : null;
        })}
      </ul>
    );
  };

  return (
    <>
      <Row justify="end">
        <Col>
          <Space>
            {hasPermission('SCHEDULE_IMPORT') &&
              <Upload {...excelUploadProps} customRequest={clickImportCalendarFile}>
                <ReButton iconName='import'>
                  {buttonT.IMPORT}
                </ReButton>
              </Upload>
            }

            {hasPermission('SCHEDULE_EXPORT') &&
              <ReButton iconName='export' onClick={clickUserExport} attributeObject={{ type: 'default' }}>
                {buttonT.EXPORT}
              </ReButton>
            }

            {hasPermission('SCHEDULE_IMPORT') &&
              <ReButton
                iconName='import'
                onClick={() => setIsModalBatchDeleteVisible(true)}
                attributeObject={{ type: 'danger' }}
              >
                {`${buttonT.BATCH}${buttonT.DELETE}`}
              </ReButton>
            }
          </Space>
        </Col>
      </Row>

      <Row gutter={[8, 0]} justify="center" className="schedule-search-bar">
        <Col>
          <Typography.Title level={3}>
            {getMoment(startTime).format(dateTimeFormat5)}
          </Typography.Title>
        </Col>

        <Col span={24}>
          <div className="schedule-type-example-container">
            <div className="schedule-type-example">
              <div className="schedule-type-example-color threeLobby" />
              <div className="schedule-type-example-text">{scheduleT.ALL}</div>
            </div>
            <div className="schedule-type-example">
              <div className="schedule-type-example-color library" />
              <div className="schedule-type-example-text">{scheduleT.STUDENT}</div>
            </div>
            <div className="schedule-type-example">
              <div className="schedule-type-example-color performanceHall" />
              <div className="schedule-type-example-text">{scheduleT.TEACHER}</div>
            </div>
          </div>
        </Col>
      </Row>

      <Row gutter={[8, 0]} justify="center">
        <Col span={22}>
          <Calendar
            dateFullCellRender={(date) => {
              const { isDayVacationDay } = getDayVacationDateParams({ specialDateArray, startDate: date });
              const { isDayMakeUpDay } = getDayMakeUpDateParams({ specialDateArray, startDate: date });
              const listData = getDateCellDataArray(date, dataArray);
              const isShowHoliday = listData.some((item: any) => item.isShowHoliday);
              const isMakeUp = isDayMakeUpDay ? '(補班)' : '';
              const isVacation = isDayVacationDay ? '(補假)' : '';

              return (
                <div
                  className={cx("ant-picker-cell-inner ant-picker-calendar-date", {
                    'schedule-date-holiday': isShowHoliday,
                  })}
                >
                  <div className="ant-picker-calendar-date-value">
                    {date.format(dateTimeFormat7)}
                    {isMakeUp && <Typography.Text type="danger">{isMakeUp}</Typography.Text>}
                    {isVacation && <Typography.Text type="success">{isVacation}</Typography.Text>}
                  </div>

                  <div className="ant-picker-calendar-date-content">
                    {dateCellRender(date)}
                  </div>
                </div>
              );
            }}
            onSelect={clickCreate}
            headerRender={({ value, type, onChange, onTypeChange }) =>
              <Row justify="end">
                <Col>
                  <DatePicker
                    picker="month"
                    allowClear={false}
                    disabledDate={disabledMonth(23, 11)}
                    defaultValue={startThisMonth}
                    onChange={(month) => changeMonth(month, onChange)}
                  />
                </Col>
              </Row>
            }
          />
        </Col>
      </Row>

      {isModalVisible &&
        <ReModal
          title={scheduleT.ANNOUNCEMENT}
          onCancel={() => toggleModal()}
          visible={isModalVisible}
          onOk={(data: any) => getOnOkFunction(handleType, data, okFunction)}
          CollectedForm={ModalInfo}
          width='900px'
          formProps={{
            selectedDate,
            dataItemInfo,
            handleType,
            clickDelete: clickDelete,
            filterDepartmentArray,
            roleId: roleId,
          }}
          Footer={roleId === 2 && null}
        />
      }

      {isModalBatchDeleteVisible &&
        <ReModal
          title={`${buttonT.BATCH}${buttonT.DELETE}`}
          onCancel={() => setIsModalBatchDeleteVisible(!isModalBatchDeleteVisible)}
          visible={isModalBatchDeleteVisible}
          onOk={(data: any) => deleteCalendarInfoFunction([...data.idArray], 'batch')}
          CollectedForm={ModalBatchDelete}
          width='900px'
          formProps={{
            dataArray,
          }}
          Footer={roleId === 2 && null}
        />
      }
    </>
  );
}

export default Schedule;